import { useSyncExternalStore } from 'react'

import channelManifest from './channelManifest.json'

export type DesignSystemChannel = 'live' | 'latest'

export type DesignSystemChannelVersion = {
  sourceRepo: string
  sourceSha: string | null
  releaseTag: string | null
  manifestHash: string | null
  label: string
  promotedAt: string | null
  syncedAt: string | null
}

export type DesignSystemChannelManifest = {
  schemaVersion: 1
  defaultChannel: DesignSystemChannel
  channels: Record<DesignSystemChannel, DesignSystemChannelVersion>
}

export const DESIGN_SYSTEM_CHANNEL_STORAGE_KEY = 'EXT_DESIGN_SYSTEM_CHANNEL'
export const DESIGN_SYSTEM_CHANNEL_CHANGE_EVENT =
  'ext-design-system-channel-change'

const manifest = channelManifest as DesignSystemChannelManifest

export function getDesignSystemVersions(): DesignSystemChannelManifest {
  return manifest
}

export function isDesignSystemChannel(
  value: unknown,
): value is DesignSystemChannel {
  return value === 'live' || value === 'latest'
}

export function getDesignSystemChannel(): DesignSystemChannel {
  if (typeof window === 'undefined') {
    return manifest.defaultChannel
  }

  const storedChannel = window.localStorage.getItem(
    DESIGN_SYSTEM_CHANNEL_STORAGE_KEY,
  )

  return isDesignSystemChannel(storedChannel)
    ? storedChannel
    : manifest.defaultChannel
}

export function setDesignSystemChannel(channel: DesignSystemChannel): void {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(DESIGN_SYSTEM_CHANNEL_STORAGE_KEY, channel)
  window.dispatchEvent(
    new CustomEvent(DESIGN_SYSTEM_CHANNEL_CHANGE_EVENT, {
      detail: { channel },
    }),
  )
}

export function subscribeToDesignSystemChannel(
  callback: (channel: DesignSystemChannel) => void,
): () => void {
  if (typeof window === 'undefined') {
    return () => {}
  }

  const handleChange = (event?: Event) => {
    if (
      event instanceof StorageEvent &&
      event.key !== DESIGN_SYSTEM_CHANNEL_STORAGE_KEY
    ) {
      return
    }

    callback(getDesignSystemChannel())
  }

  window.addEventListener(DESIGN_SYSTEM_CHANNEL_CHANGE_EVENT, handleChange)
  window.addEventListener('storage', handleChange)

  return () => {
    window.removeEventListener(DESIGN_SYSTEM_CHANNEL_CHANGE_EVENT, handleChange)
    window.removeEventListener('storage', handleChange)
  }
}

export function useResolvedDesignSystemComponent<TComponent>(
  liveComponent: TComponent,
  latestComponent?: TComponent,
): TComponent {
  const channel = useSyncExternalStore(
    subscribeToDesignSystemChannel,
    getDesignSystemChannel,
    () => manifest.defaultChannel,
  )

  return channel === 'latest' && latestComponent
    ? latestComponent
    : liveComponent
}

/**
 * Resolves the selected design system component for non-React contexts.
 * Do not call this at module scope in generated React facades because it will
 * freeze the selected channel at import time. Use
 * `useResolvedDesignSystemComponent` inside React components instead.
 */
export function resolveDesignSystemComponent<TComponent>(
  liveComponent: TComponent,
  latestComponent?: TComponent,
): TComponent {
  if (getDesignSystemChannel() === 'latest' && latestComponent) {
    return latestComponent
  }

  return liveComponent
}
